import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Switch } from './ui/switch';
import { 
  ArrowLeft,
  Shield,
  Key,
  Download,
  AlertTriangle,
  CheckCircle,
  Smartphone,
  Eye,
  EyeOff,
  Lock,
  Activity,
  X,
  Mail
} from 'lucide-react';
import { useAuth } from '../context/CompleteAuthContext';
import { toast } from 'sonner';

const SecuritySettings = ({ onBack }) => {
  const { user, exportUserData, logout } = useAuth();
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [showExportPassword, setShowExportPassword] = useState(false);
  const [exportPassword, setExportPassword] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [twoFAMethod, setTwoFAMethod] = useState(''); // 'email', 'sms', or ''
  const [selectedMethod, setSelectedMethod] = useState('email');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');

  // Sync 2FA status from user object
  React.useEffect(() => {
    if (user) {
      setMfaEnabled(user.two_fa_enabled || false);
    }
  }, [user]);

  const securityFeatures = [
    {
      title: 'Google OAuth Authentication',
      description: 'Secure login with your Google account',
      status: 'enabled',
      icon: Shield,
      enabled: true
    },
    {
      title: 'HTTPS Encryption',
      description: 'All data transmitted over secure connections',
      status: 'enabled',
      icon: Lock,
      enabled: true
    },
    {
      title: 'Two-Factor Authentication',
      description: 'Extra security for your account',
      status: mfaEnabled ? 'enabled' : 'disabled',
      icon: Smartphone,
      enabled: mfaEnabled,
      toggle: true
    },
    {
      title: 'Session Management',
      description: 'Automatic logout after 7 days of inactivity',
      status: 'enabled',
      icon: Activity,
      enabled: true
    }
  ];

  const handleMfaToggle = async (enabled) => {
    if (enabled) {
      // Show 2FA setup modal instead of directly enabling
      setShow2FASetup(true);
    } else {
      // TODO: Implement disable 2FA functionality
      toast.info('Two-Factor Authentication disabled');
      setMfaEnabled(false);
    }
  };

  const handleSetup2FA = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/auth/enable-2fa`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ 
          password: currentPassword,
          method: selectedMethod,
          phone_number: selectedMethod === 'sms' ? phoneNumber : null
        })
      });

      if (response.ok) {
        const data = await response.json();
        setTwoFAMethod(selectedMethod);
        
        // Automatically send the first verification code
        await handleSendCode();
        
        toast.success(`2FA setup initiated. Check your ${selectedMethod === 'email' ? 'email' : 'phone'} for the verification code.`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to setup 2FA');
      }
    } catch (error) {
      toast.error('Failed to setup 2FA');
    }
  };

  const handleSendCode = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/auth/send-2fa-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Verification code sent!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send verification code');
      }
    } catch (error) {
      toast.error('Failed to send verification code');
    }
  };

  const handleResendCode = async () => {
    await handleSendCode();
  };

  const handleVerify2FA = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/auth/verify-2fa`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ code: verificationCode })
      });

      if (response.ok) {
        toast.success('Two-Factor Authentication enabled successfully!');
        setMfaEnabled(true);
        setShow2FASetup(false);
        setQrData(null);
        setVerificationCode('');
        setCurrentPassword('');
        // Refresh user data to get updated 2FA status
        window.location.reload();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Invalid verification code');
      }
    } catch (error) {
      toast.error('Verification failed');
    }
  };

  const handleExportData = async () => {
    setIsExporting(true);
    try {
      const success = await exportUserData(exportPassword || null);
      if (success) {
        setExportPassword('');
      }
    } finally {
      setIsExporting(false);
    }
  };

  const getStatusBadge = (status, enabled) => {
    if (enabled) {
      return <Badge className="bg-green-100 text-green-800">Enabled</Badge>;
    } else {
      return <Badge variant="secondary" className="bg-gray-100 text-gray-600">Disabled</Badge>;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center space-x-4">
          <Button variant="ghost" onClick={onBack} size="sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-xl font-bold text-gray-900">Security Settings</h1>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-8">
        {/* Account Security Overview */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Shield className="w-5 h-5" />
              <span>Account Security</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center space-x-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <div>
                <h3 className="font-semibold text-green-900">Account Secured</h3>
                <p className="text-sm text-green-700">
                  Your account is protected with industry-standard security measures
                </p>
              </div>
            </div>

            <div className="grid gap-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Account Email:</span>
                <span className="text-sm text-gray-600">{user?.email}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Account Created:</span>
                <span className="text-sm text-gray-600">Current session</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Last Login:</span>
                <span className="text-sm text-gray-600">Now</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Security Features */}
        <Card>
          <CardHeader>
            <CardTitle>Security Features</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {securityFeatures.map((feature, index) => (
              <div key={index}>
                <div className="flex items-center justify-between p-4 rounded-lg border border-gray-200">
                  <div className="flex items-center space-x-4">
                    <div className={`p-2 rounded-full ${feature.enabled ? 'bg-blue-100' : 'bg-gray-100'}`}>
                      <feature.icon className={`w-5 h-5 ${feature.enabled ? 'text-blue-600' : 'text-gray-500'}`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{feature.title}</h3>
                      <p className="text-sm text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    {getStatusBadge(feature.status, feature.enabled)}
                    {feature.toggle && (
                      <Switch
                        checked={feature.enabled}
                        onCheckedChange={handleMfaToggle}
                      />
                    )}
                  </div>
                </div>
                {index < securityFeatures.length - 1 && <Separator className="my-4" />}
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Payment Security */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Key className="w-5 h-5" />
              <span>Payment Security</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start space-x-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-900">Stripe Integration</h3>
                <p className="text-sm text-blue-700 mt-1">
                  All payments are processed securely through Stripe. Your card information never touches our servers.
                </p>
                <ul className="mt-3 space-y-1 text-sm text-blue-700">
                  <li>• PCI DSS compliant payment processing</li>
                  <li>• Bank-level encryption for all transactions</li>
                  <li>• No card data stored on our servers</li>
                  <li>• Real-time fraud detection and prevention</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Data Export */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Download className="w-5 h-5" />
              <span>Data Export</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600">
              Export all your data including expenses, payments, and group information. 
              This allows you to migrate to another service if needed.
            </p>

            <div className="space-y-4 p-4 border border-gray-200 rounded-lg">
              <div className="flex items-center space-x-2">
                <label htmlFor="export-password" className="text-sm font-medium">
                  Export Password (Optional):
                </label>
              </div>
              <div className="flex space-x-2">
                <div className="relative flex-1">
                  <input
                    id="export-password"
                    type={showExportPassword ? 'text' : 'password'}
                    value={exportPassword}
                    onChange={(e) => setExportPassword(e.target.value)}
                    placeholder="Leave blank for unencrypted export"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowExportPassword(!showExportPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                  >
                    {showExportPassword ? (
                      <EyeOff className="w-4 h-4 text-gray-500" />
                    ) : (
                      <Eye className="w-4 h-4 text-gray-500" />
                    )}
                  </button>
                </div>
                <Button 
                  onClick={handleExportData}
                  disabled={isExporting}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {isExporting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Exporting...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Export Data
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                Data will be exported as a JSON file containing all your information.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Danger Zone */}
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              <span>Danger Zone</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <h3 className="font-semibold text-red-900 mb-2">Sign Out</h3>
                <p className="text-sm text-red-700 mb-3">
                  This will log you out of your account and clear your session.
                </p>
                <Button 
                  variant="outline" 
                  onClick={logout}
                  className="border-red-300 text-red-700 hover:bg-red-50"
                >
                  Sign Out
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 2FA Setup Modal */}
      {show2FASetup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Setup Two-Factor Authentication</h2>
              <button
                onClick={() => setShow2FASetup(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {!twoFAMethod ? (
              // Step 1: Choose 2FA method and enter password
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Choose how you'd like to receive verification codes for two-factor authentication.
                </p>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Verification Method
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="2faMethod"
                        value="email"
                        checked={selectedMethod === 'email'}
                        onChange={(e) => setSelectedMethod(e.target.value)}
                        className="mr-3"
                      />
                      <Mail className="w-5 h-5 mr-2 text-blue-600" />
                      <div>
                        <div className="font-medium">Email Verification</div>
                        <div className="text-sm text-gray-500">Send codes to your email address</div>
                      </div>
                    </label>
                    
                    <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="2faMethod"
                        value="sms"
                        checked={selectedMethod === 'sms'}
                        onChange={(e) => setSelectedMethod(e.target.value)}
                        className="mr-3"
                      />
                      <Smartphone className="w-5 h-5 mr-2 text-green-600" />
                      <div>
                        <div className="font-medium">SMS Verification</div>
                        <div className="text-sm text-gray-500">Send codes to your mobile number</div>
                      </div>
                    </label>
                  </div>
                </div>

                {selectedMethod === 'sms' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mobile Number
                    </label>
                    <input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Current Password
                  </label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter your current password"
                  />
                </div>

                <Button 
                  onClick={handleSetup2FA} 
                  className="w-full"
                  disabled={!selectedMethod || !currentPassword || (selectedMethod === 'sms' && !phoneNumber)}
                >
                  Setup {selectedMethod === 'email' ? 'Email' : 'SMS'} Verification
                </Button>
              </div>
            ) : (
              // Step 2: Verify with sent code
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-blue-800 font-semibold">Verification Code Sent</p>
                  <p className="text-sm text-blue-700 mt-1">
                    {twoFAMethod === 'email' 
                      ? `We've sent a 6-digit code to your email address` 
                      : `We've sent a 6-digit code to ${phoneNumber}`
                    }
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Verification Code
                  </label>
                  <input
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-lg tracking-widest font-mono"
                    placeholder="000000"
                    maxLength={6}
                  />
                </div>

                <Button 
                  onClick={handleVerify2FA} 
                  className="w-full"
                  disabled={verificationCode.length !== 6}
                >
                  Complete Setup
                </Button>

                <div className="flex space-x-2">
                  <Button 
                    onClick={handleResendCode} 
                    variant="outline"
                    className="flex-1"
                  >
                    Resend Code
                  </Button>
                  <Button 
                    onClick={() => {
                      setTwoFAMethod('');
                      setVerificationCode('');
                    }} 
                    variant="outline"
                    className="flex-1"
                  >
                    Change Method
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SecuritySettings;